"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"

import { useQualitySections } from "@/hooks/use-quality-presentations"


import type { ReactNode } from "react"
import { ArrowRightLeft, Clock, UserCheck, UserPlus, Users, Zap, type LucideIcon } from "lucide-react"

import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { MetricKind } from "@/lib/analytics/deltas"
import { formatShare } from "@/lib/analytics/format"
import { NO_VALUE, share, type ServiceMetricKey } from "@/lib/conversation-quality/metric-definitions"
import type { ServiceMetricsResult } from "@/server-actions/conversation-quality"

import { formatRatio, leader, MetricFaceOff, PairedBars } from "./metric-comparison"
import { DefinedTerm, EmptyNote, SectionCard } from "./section-parts"
import { useTranslations } from "next-intl"
import { useQualityCopy } from "@/components/i18n/quality-copy-context"




const percent = (value: number | null) => formatShare(value, NO_VALUE)

type Fate = ServiceMetricsResult["current"]["escalations"]

const HANDOVER_TILES_GRID = "grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5"

/** Share of one fate over its population, now against before, compared where a window exists. */
function RateTile({
  data,
  metric,
  icon,
  of,
  over,
  kind,
  subtitle,
}: {
  data: ServiceMetricsResult;
  metric: ServiceMetricKey;
  icon: LucideIcon;
  of: (fate: Fate) => number;
  over: (fate: Fate) => number;
  /** Which direction reads as good — a transfer is an expense, a pick-up a success. */
  kind: MetricKind;
  subtitle: ReactNode;
}) {
  const { shareComparison } = useQualitySections()

  const { SERVICE_METRIC_DEFINITIONS } = useQualityCopy()

  const now = data.current.escalations
  const before = data.previous.escalations
  const current = share(of(now), over(now))

  return (
    <KpiCard
      label={SERVICE_METRIC_DEFINITIONS[metric].label}
      tooltip={SERVICE_METRIC_DEFINITIONS[metric].definition}
      icon={icon}
      formattedValue={percent(current)}
      {...shareComparison(current, data.hasComparison ? { of: of(before), over: over(before) } : null, kind)}
      subtitle={subtitle}
    />
  )
}

function answered(fate: Fate): number {
  return fate.pickedUp + fate.answeredByEmail
}

/**
 * Where the team replied; email is named only where the mailbox answered any, with what that count can claim. One
 * element either way, its text whole, so a translated page can switch between the two.
 */
function Replies({ fate: { pickedUp, answeredByEmail } }: { fate: Fate }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { SERVICE_METRIC_DEFINITIONS } = useQualityCopy()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsServiceMetricSections")

  if (answeredByEmail === 0) return <span>{<span key={tUi("aPersonRepliedInValue0", { value0: String(pickedUp.toLocaleString(uiFormattingLocale)) })}>{tUi("aPersonRepliedInValue0", { value0: String(pickedUp.toLocaleString(uiFormattingLocale)) })}</span>}</span>

  const { label, definition } = SERVICE_METRIC_DEFINITIONS.answeredByEmail
  return (
    <span>
      <span>{<span key={tUi("repliedInChatValue0", { value0: String(pickedUp.toLocaleString(uiFormattingLocale)) })}>{tUi("repliedInChatValue0", { value0: String(pickedUp.toLocaleString(uiFormattingLocale)) })}</span>}</span>
      <DefinedTerm label={tUi("value0Value1", { value0: String(label), value1: String(answeredByEmail.toLocaleString(uiFormattingLocale)) })} definition={definition} />
    </span>
  )
}

/** How often, how many were answered, how fast, and how many times the team came unasked. */
export function HandoverTiles({ data }: { data: ServiceMetricsResult | undefined }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { duration } = useQualitySections()

  const { AI_HANDLED_POPULATION, SERVICE_METRIC_DEFINITIONS } = useQualityCopy()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsServiceMetricSections")

  if (!data) {
    return (
      <div className={HANDOVER_TILES_GRID}>
        {Array.from({ length: 5 }, (_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const now = data.current.escalations
  return (
    <div className={HANDOVER_TILES_GRID}>
      <RateTile
        data={data}
        metric="transferRate"
        icon={UserPlus}
        of={(fate) => fate.transferred}
        over={(fate) => fate.aiHandledSessions}
        kind="negative"
        subtitle={tUi("value0OfValue1Value2", { value0: String(now.transferred.toLocaleString(uiFormattingLocale)), value1: String(now.aiHandledSessions.toLocaleString(uiFormattingLocale)), value2: String(AI_HANDLED_POPULATION) })}
      />
      <RateTile
        data={data}
        metric="escalationRate"
        icon={ArrowRightLeft}
        of={(fate) => fate.escalated}
        over={(fate) => fate.aiHandledSessions}
        kind="negative"
        subtitle={tUi("value0OfValue1Value2", { value0: String(now.escalated.toLocaleString(uiFormattingLocale)), value1: String(now.aiHandledSessions.toLocaleString(uiFormattingLocale)), value2: String(AI_HANDLED_POPULATION) })}
      />
      <RateTile
        data={data}
        metric="pickedUp"
        icon={UserCheck}
        of={answered}
        over={(fate) => fate.escalatedSessions}
        kind="positive"
        subtitle={
          <>
            <Replies fate={now} />
            <span>{<span key={tUi("ofValue0", { value0: String(now.escalatedSessions.toLocaleString(uiFormattingLocale)) })}>{tUi("ofValue0", { value0: String(now.escalatedSessions.toLocaleString(uiFormattingLocale)) })}</span>}</span>
          </>
        }
      />
      <KpiCard
        label={SERVICE_METRIC_DEFINITIONS.timeToHuman.label}
        tooltip={SERVICE_METRIC_DEFINITIONS.timeToHuman.definition}
        icon={Clock}
        formattedValue={duration(now.timeToHumanMedianMs)}
        subtitle={
          now.timeToHumanMedianMs === null
            ? "no escalation was answered in the conversation"
            : `over ${now.pickedUp.toLocaleString(uiFormattingLocale)} answered in the conversation`
        }
      />
      <KpiCard
        label={SERVICE_METRIC_DEFINITIONS.operatorTakeovers.label}
        tooltip={SERVICE_METRIC_DEFINITIONS.operatorTakeovers.definition}
        icon={Users}
        formattedValue={now.operatorTakeovers.toLocaleString(uiFormattingLocale)}
        subtitle={tUi("aPersonSteppedInUnasked")}
      />
    </div>
  )
}

/** The two halves carry different captions because they are attributed differently. */
export function ServiceMetrics({ data }: { data: ServiceMetricsResult | undefined }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { sessionCount } = useQualitySections()

  const { SERVICE_METRIC_POPULATION, ATTRIBUTION_CAPTIONS, ACTOR_LABELS, COHORT_LABELS } = useQualityCopy()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsServiceMetricSections")

  return (
    <SectionCard
      title={tUi("serviceMetrics")}
      subtitle={tUi("value0Value12", { value0: String(SERVICE_METRIC_POPULATION), value1: String(data ? ` — ${data.current.escalations.sessions.toLocaleString(uiFormattingLocale)} in this period` : "") })}
    >
      {!data ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <div className="space-y-4">
          <FirstAnswerHeadline data={data} />
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
            <ComparisonGroup
              caption={ATTRIBUTION_CAPTIONS.phase}
              footer={`${data.current.messages.ai.toLocaleString(uiFormattingLocale)} AI messages · ${data.current.messages.team.toLocaleString(uiFormattingLocale)} team messages`}
            >
              <MetricFaceOff
                metric="firstResponse"
                leftLabel={ACTOR_LABELS.ai}
                rightLabel={ACTOR_LABELS.team}
                left={data.current.ai.firstMedianMs}
                right={data.current.team.firstMedianMs}
              />
              <MetricFaceOff
                metric="replyTime"
                leftLabel={ACTOR_LABELS.ai}
                rightLabel={ACTOR_LABELS.team}
                left={data.current.ai.replyMedianMs}
                right={data.current.team.replyMedianMs}
              />
            </ComparisonGroup>
            <ComparisonGroup
              caption={ATTRIBUTION_CAPTIONS.cohort}
              footer={`${sessionCount(data.current.aiOnly.sessions)} the agent handled alone · ${sessionCount(data.current.teamInvolved.sessions)} your team took part in`}
            >
              <MetricFaceOff
                metric="handleTime"
                leftLabel={COHORT_LABELS.aiOnly}
                rightLabel={COHORT_LABELS.teamInvolved}
                left={data.current.aiOnly.handleTimeMedianMs}
                right={data.current.teamInvolved.handleTimeMedianMs}
              />
              <PairedBars
                metric="customerEffort"
                leftLabel={COHORT_LABELS.aiOnly}
                rightLabel={COHORT_LABELS.teamInvolved}
                left={data.current.aiOnly.customerMessagesAvg}
                right={data.current.teamInvolved.customerMessagesAvg}
                format={formatEffort}
              />
            </ComparisonGroup>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

/** The period's loudest reading, said once: how soon a customer hears back is the first thing anyone asks. */
function FirstAnswerHeadline({ data }: { data: ServiceMetricsResult }) {
  const { ACTOR_LABELS } = useQualityCopy()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsServiceMetricSections")

  const ahead = leader([
    { name: ACTOR_LABELS.ai, value: data.current.ai.firstMedianMs },
    { name: ACTOR_LABELS.team, value: data.current.team.firstMedianMs },
  ])
  if (!ahead) return null

  const behind = ahead.name === ACTOR_LABELS.ai ? ACTOR_LABELS.team : ACTOR_LABELS.ai
  return (
    <p className="flex items-center gap-2 rounded-md bg-green-600/10 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400">
      <Zap className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{<span key={tUi("value0AnswersFirstValue1FasterThanValue2", { value0: String(ahead.name), value1: String(formatRatio(ahead.ratio)), value2: String(midSentence(behind)) })}>{tUi("value0AnswersFirstValue1FasterThanValue2", { value0: String(ahead.name), value1: String(formatRatio(ahead.ratio)), value2: String(midSentence(behind)) })}</span>}</span>
    </p>
  )
}

/** «Your team» opens a sentence, not the middle of one. */
function midSentence(label: string): string {
  return label.replace(/^Your /, "your ")
}

/** A caption naming how its metrics are attributed, and a footer counting what they were measured over. */
function ComparisonGroup({
  caption,
  footer,
  children,
}: {
  caption: string;
  footer: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{caption}</p>
      {children}
      <p className="text-xs text-muted-foreground">{footer}</p>
    </div>
  )
}

export function TeamTable({ data }: { data: ServiceMetricsResult | undefined }) {
  const { duration } = useQualitySections()

  const { SERVICE_METRIC_DEFINITIONS } = useQualityCopy()

  const tUi = useTranslations("ConversationQuality.ConversationQualitySectionsServiceMetricSections")

  return (
    <SectionCard title={tUi("team")} subtitle={tUi("whoAnsweredAndHowFast")}>
      {!data ? (
        <Skeleton className="h-24 w-full" />
      ) : data.operators.length === 0 ? (
        <EmptyNote text={tUi("noSessionsAPersonTookPartIn")} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-0"><span key={tUi("operator")}>{tUi("operator")}</span></TableHead>
              <TableHead className="text-right"><span key={tUi("sessions")}>{tUi("sessions")}</span></TableHead>
              <TableHead className="text-right">
                {SERVICE_METRIC_DEFINITIONS.firstResponse.label}
              </TableHead>
              <TableHead className="text-right">{SERVICE_METRIC_DEFINITIONS.replyTime.label}</TableHead>
              <TableHead className="pr-0 text-right">
                {SERVICE_METRIC_DEFINITIONS.escalationsHandled.label}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.operators.map((operator) => (
              <TableRow key={`${operator.operatorId ?? ""}|${operator.operatorName ?? ""}`}>
                <TableCell className="pl-0">
                  {operator.operatorName ?? (
                    <div className="min-w-0">
                      <p><span key={tUi("repliedOutsideTheInbox")}>{tUi("repliedOutsideTheInbox")}</span></p>
                      <p className="text-xs text-muted-foreground">
                        <span key={tUi("inTheChannelSOwnAppOrA")}>{tUi("inTheChannelSOwnAppOrA")}</span></p>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{operator.sessions}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {duration(operator.firstResponseMedianMs)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {duration(operator.replyMedianMs)}
                </TableCell>
                <TableCell className="pr-0 text-right tabular-nums">
                  {operator.escalationsPickedUp}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  )
}

function formatEffort(value: number): string {
  return value.toFixed(1)
}
